package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import co.uk.sacursoftware.ssinventory.util.rememberPlatformImagePicker
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.datetime.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OrderDetailsScreen(
    order: Order,
    onBack: () -> Unit,
    onOrderUpdated: () -> Unit
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var orderItems by remember { mutableStateOf<List<OrderItem>>(emptyList()) }
    var itemNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var supplierCatalog by remember { mutableStateOf<List<Item>>(emptyList()) }
    var orderImages by remember { mutableStateOf<List<String>>(order.images ?: emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isEditing by remember { mutableStateOf(false) }

    // Maps for form inputs: itemId -> quantity string
    val receivedQuantities = remember { mutableStateMapOf<String, String>() }
    val orderedQuantities = remember { mutableStateMapOf<String, String>() }

    var supplierName by remember { mutableStateOf(order.supplierName ?: "") }
    var resolvedSupplier by remember { mutableStateOf(Supplier(id = order.supplierId, name = order.supplierName ?: "Supplier")) }
    var itemStocks by remember { mutableStateOf<Map<String, Double>>(emptyMap()) }
    var showAddItemDialog by remember { mutableStateOf(false) }
    var showExportDialog by remember { mutableStateOf<List<OrderItem>?>(null) }
    var showReceiveModal by remember { mutableStateOf(false) }
    var showImageViewerDialog by remember { mutableStateOf<String?>(null) }
    var showDeleteOrderDialog by remember { mutableStateOf(false) }
    var itemIdToRemove by remember { mutableStateOf<String?>(null) }

    fun refresh() {
        scope.launch {
            isLoading = true
            
            val itemsResult = repository.getItems(order.supplierId)
            if (itemsResult.isSuccess) {
                val catalog = itemsResult.getOrThrow()
                supplierCatalog = catalog
                itemNames = catalog.associate { it.id to it.name }
            }

            repository.getSuppliers().onSuccess { suppliers ->
                suppliers.firstOrNull { it.id == order.supplierId }?.let { supplier ->
                    supplierName = supplier.name
                    resolvedSupplier = supplier
                }
            }

            repository.getStockCounts(order.supplierId).onSuccess { counts ->
                counts.firstOrNull()?.let { lastCount ->
                    repository.getStockCountDetails(lastCount.id).onSuccess { details ->
                        itemStocks = details.associate { it.itemId to it.countedRealAmount }
                    }
                }
            }

            repository.getImages(order.id).onSuccess { imgs ->
                orderImages = imgs.map { it.imageUrl }
            }

            repository.getOrderItems(order.id).onSuccess { items ->
                orderItems = items
                items.forEach { item ->
                    receivedQuantities[item.itemId] = formatWhole(item.qtyReceived ?: item.qtyOrdered)
                    val ordDisplay = formatWhole(item.qtyOrdered)
                    orderedQuantities[item.itemId] = ordDisplay
                }
            }
            isLoading = false
        }
    }

    LaunchedEffect(order.id) {
        refresh()
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                TopAppBar(
                    title = { 
                        Column {
                            Text(
                                supplierName.ifBlank { "Order" },
                                style = MaterialTheme.typography.titleMedium,
                                maxLines = 1
                            )
                            Text(
                                buildString {
                                    append(DateTimeUtils.formatTitleDate(order.createdAt))
                                    if (isEditing) append(" · Editing")
                                    else append(" · ${order.status}")
                                },
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        OverflowActionsMenu(
                            actions = buildList {
                                add(OverflowAction("Print") {
                                    val itemsForPrint = if (isEditing) {
                                        orderItems.mapNotNull { item ->
                                            val qty = orderedQuantities[item.itemId]?.toDoubleOrNull() ?: 0.0
                                            if (qty > 0) item.copy(qtyOrdered = qty) else null
                                        }
                                    } else {
                                        orderItems
                                    }
                                    showExportDialog = itemsForPrint
                                })
                                if (order.status == "Pending") {
                                    if (isEditing) {
                                        add(OverflowAction("Cancel edit") {
                                            isEditing = false
                                            refresh()
                                        })
                                    } else {
                                        add(OverflowAction("Edit") { isEditing = true })
                                    }
                                }
                                if (!isEditing) {
                                    add(OverflowAction("Delete", destructive = true) {
                                        showDeleteOrderDialog = true
                                    })
                                }
                            }
                        )
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        if (orderImages.isNotEmpty()) {
                            Text(
                                "Delivery Photos (${orderImages.size})",
                                style = MaterialTheme.typography.titleSmall,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                            )
                            LazyRow(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                items(orderImages) { url ->
                                    Card(
                                        modifier = Modifier.size(100.dp).clickable { showImageViewerDialog = url },
                                        shape = MaterialTheme.shapes.small
                                    ) {
                                        Box(modifier = Modifier.fillMaxSize()) {
                                            RemoteImageView(url = url, modifier = Modifier.fillMaxSize())
                                        }
                                    }
                                }
                            }
                        }

                        LazyColumn(modifier = Modifier.weight(1f)) {
                            items(orderItems) { item ->
                                if (isEditing) {
                                    EditableOrderItemRow(
                                        itemName = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}",
                                        supplierName = order.supplierName ?: "",
                                        orderedQty = orderedQuantities[item.itemId] ?: "0",
                                        onOrderedQtyChange = { orderedQuantities[item.itemId] = it },
                                        onRemove = { itemIdToRemove = item.itemId }
                                    )
                                } else {
                                    OrderItemRow(
                                        item = item,
                                        itemName = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}",
                                        orderStatus = order.status,
                                        receivedQty = receivedQuantities[item.itemId] ?: "0",
                                        onReceivedQtyChange = { receivedQuantities[item.itemId] = it }
                                    )
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            }

                            if (isEditing) {
                                item {
                                    Box(modifier = Modifier.fillMaxWidth().padding(16.dp), contentAlignment = Alignment.Center) {
                                        OutlinedButton(
                                            onClick = { showAddItemDialog = true },
                                            shape = MaterialTheme.shapes.medium
                                        ) {
                                            Text("+ Add Item to Order")
                                        }
                                    }
                                }
                            }
                        }

                        if (userRole == UserRole.ADMIN) {
                            val totalValuation = remember(orderItems, supplierCatalog) {
                                val map = supplierCatalog.associateBy { it.id }
                                var total = 0.0
                                orderItems.forEach { oi ->
                                    val item = map[oi.itemId]
                                    if (item != null) {
                                        total += oi.qtyOrdered * item.price
                                    }
                                }
                                total
                            }
                            Surface(
                                color = MaterialTheme.colorScheme.primaryContainer,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text("TOTAL ORDER VALUE", fontWeight = FontWeight.Bold)
                                    Text("£${formatDecimal(totalValuation)}", fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }

                        if (order.status == "Pending") {
                            if (isEditing) {
                                Button(
                                    onClick = {
                                        scope.launch {
                                            val updatedItems = orderItems.mapNotNull { item ->
                                                val qty = orderedQuantities[item.itemId]?.toDoubleOrNull() ?: 0.0
                                                if (qty > 0) {
                                                    item.copy(qtyOrdered = qty)
                                                } else null
                                            }

                                            if (updatedItems.isEmpty()) {
                                                snackbarHostState.showSnackbar("Order must contain at least one item")
                                                return@launch
                                            }

                                            val request = EditOrderRequest(
                                                id = order.id,
                                                status = "Pending",
                                                items = updatedItems
                                            )
                                            repository.editOrder(request).fold(
                                                onSuccess = { resp ->
                                                    if (resp.error == null) {
                                                        isEditing = false
                                                        showExportDialog = updatedItems
                                                        refresh()
                                                    } else {
                                                        snackbarHostState.showSnackbar("Error: ${resp.error}")
                                                    }
                                                },
                                                onFailure = {
                                                    snackbarHostState.showSnackbar("Network error: ${it.message}")
                                                }
                                            )
                                        }
                                    },
                                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                                    shape = MaterialTheme.shapes.medium,
                                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                                ) {
                                    Text("SAVE & EXPORT ORDER", fontWeight = FontWeight.Bold)
                                }
                            } else {
                                Button(
                                    onClick = { showReceiveModal = true },
                                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                                    shape = MaterialTheme.shapes.medium
                                ) {
                                    Text("MARK AS RECEIVED")
                                }
                            }
                        } else if (order.status == "Received" && userRole == UserRole.ADMIN) {
                            Button(
                                onClick = { showReceiveModal = true },
                                modifier = Modifier.fillMaxWidth().padding(16.dp),
                                shape = MaterialTheme.shapes.medium,
                                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
                            ) {
                                Text("EDIT RECEIVED DETAILS")
                            }
                        }
                    }
                }
            }
        }

        if (showAddItemDialog) {
            val existingItemIds = orderItems.map { it.itemId }.toSet()
            val availableItems = supplierCatalog.filter { it.id !in existingItemIds }

            AddItemToOrderDialog(
                availableItems = availableItems,
                onDismiss = { showAddItemDialog = false },
                onItemSelect = { newItem ->
                    val newItemOrder = OrderItem(
                        itemId = newItem.id,
                        qtySuggested = 0.0,
                        qtyOrdered = 1.0,
                        name = newItem.name
                    )
                    orderItems = orderItems + newItemOrder
                    orderedQuantities[newItem.id] = "1"
                    showAddItemDialog = false
                }
            )
        }

        if (showImageViewerDialog != null) {
            val imageName = showImageViewerDialog!!.substringAfterLast("/").split("?").firstOrNull() ?: "Delivery Photo"
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(androidx.compose.ui.graphics.Color.Black.copy(alpha = 0.95f))
                    .clickable(enabled = false) {},
                contentAlignment = Alignment.Center
            ) {
                Column(
                    modifier = Modifier.fillMaxSize().padding(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.SpaceBetween
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 24.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = imageName,
                            style = MaterialTheme.typography.titleMedium,
                            color = androidx.compose.ui.graphics.Color.White,
                            maxLines = 1,
                            modifier = Modifier.weight(1f)
                        )
                        IconButton(onClick = { showImageViewerDialog = null }) {
                            CloseIcon(tint = androidx.compose.ui.graphics.Color.White)
                        }
                    }

                    RemoteImageView(
                        url = showImageViewerDialog!!,
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .padding(vertical = 16.dp)
                    )

                    Spacer(modifier = Modifier.height(24.dp))
                }
            }
        }

        if (showReceiveModal) {
            OrderReceivedModal(
                orderId = order.id,
                initialReceivedAt = order.receivedAt,
                initialImages = orderImages,
                onDismissWithoutDate = {
                    showReceiveModal = false
                    scope.launch {
                        val updatedItems = orderItems.map { item ->
                            item.copy(qtyReceived = receivedQuantities[item.itemId]?.toDoubleOrNull() ?: 0.0)
                        }
                        val request = EditOrderRequest(
                            id = order.id,
                            status = "Received",
                            receivedAt = null,
                            receiveImageUrl = orderImages.firstOrNull(),
                            isActionMissing = 1,
                            images = orderImages,
                            items = updatedItems
                        )
                        repository.editOrder(request).onSuccess {
                            onOrderUpdated()
                        }
                    }
                },
                onConfirm = { receivedAt, images, isActionMissing ->
                    showReceiveModal = false
                    scope.launch {
                        val updatedItems = orderItems.map { item ->
                            item.copy(qtyReceived = receivedQuantities[item.itemId]?.toDoubleOrNull() ?: 0.0)
                        }
                        val request = EditOrderRequest(
                            id = order.id,
                            status = "Received",
                            receivedAt = receivedAt,
                            receiveImageUrl = images.firstOrNull(),
                            isActionMissing = isActionMissing,
                            images = images,
                            items = updatedItems
                        )
                        repository.editOrder(request).onSuccess {
                            onOrderUpdated()
                        }
                    }
                }
            )
        }

        showExportDialog?.let { exportItems ->
            OrderExportDialog(
                supplier = resolvedSupplier,
                items = supplierCatalog,
                itemNames = itemNames,
                itemStocks = itemStocks,
                orderItems = exportItems,
                daysCalculated = order.daysCalculated.toString(),
                onDismiss = { showExportDialog = null }
            )
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background),
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner()
            }
        }

        if (showDeleteOrderDialog) {
            ConfirmDeleteDialog(
                message = "Delete this order? This cannot be undone.",
                onConfirm = {
                    showDeleteOrderDialog = false
                    scope.launch {
                        repository.deleteOrder(order.id).onSuccess {
                            onOrderUpdated()
                        }
                    }
                },
                onDismiss = { showDeleteOrderDialog = false }
            )
        }

        itemIdToRemove?.let { itemId ->
            ConfirmDeleteDialog(
                title = "Remove item?",
                message = "Remove '${itemNames[itemId] ?: "this item"}' from the order?",
                confirmLabel = "Remove",
                onConfirm = {
                    orderItems = orderItems.filter { it.itemId != itemId }
                    orderedQuantities.remove(itemId)
                    itemIdToRemove = null
                },
                onDismiss = { itemIdToRemove = null }
            )
        }
    }
}

@Composable
fun OrderReceivedModal(
    orderId: String,
    initialReceivedAt: String? = null,
    initialImages: List<String> = emptyList(),
    onDismissWithoutDate: () -> Unit,
    onConfirm: (receivedAt: String?, images: List<String>, isActionMissing: Int) -> Unit
) {
    val scope = rememberCoroutineScope()
    val storageService = remember { FirebaseStorageService() }

    val existingImages = remember { mutableStateListOf<String>().apply { addAll(initialImages) } }
    val removedImages = remember { mutableStateListOf<String>() }
    val newImages = remember { mutableStateListOf<Pair<ByteArray, String>>() }
    var existingToRemove by remember { mutableStateOf<String?>(null) }
    var newToRemove by remember { mutableStateOf<Pair<ByteArray, String>?>(null) }

    val imagePicker = rememberPlatformImagePicker { bytes, fileName ->
        newImages.add(bytes to fileName)
    }

    val nowDate = getCurrentLocalDate()
    val defaultDate = initialReceivedAt?.substringBefore(" ") ?: "${nowDate.year}-${(nowDate.month.ordinal + 1).toString().padStart(2, '0')}-${nowDate.dayOfMonth.toString().padStart(2, '0')}"
    val defaultHour = "12"
    val defaultMinute = "00"

    var dateInput by remember { mutableStateOf(defaultDate) }
    var hourInput by remember { mutableStateOf(defaultHour) }
    var minuteInput by remember { mutableStateOf(defaultMinute) }
    
    var isUploading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    AlertDialog(
        onDismissRequest = onDismissWithoutDate,
        title = { Text("Order Received Details & Photos") },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth().heightIn(max = 500.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text("Provide received date/time and attach one or more delivery photos.", style = MaterialTheme.typography.bodyMedium)

                OutlinedTextField(
                    value = dateInput,
                    onValueChange = { dateInput = it },
                    label = { Text("Date (YYYY-MM-DD)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedTextField(
                        value = hourInput,
                        onValueChange = { hourInput = it },
                        label = { Text("Hour (00-23)") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = minuteInput,
                        onValueChange = { minuteInput = it },
                        label = { Text("Minute (00-59)") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                }

                HorizontalDivider()

                Text("Delivery Photos (${existingImages.size + newImages.size})", style = MaterialTheme.typography.titleSmall)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = { imagePicker.takePhoto() },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("Take Photo")
                    }
                    OutlinedButton(
                        onClick = { imagePicker.pickFromGallery() },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("Gallery")
                    }
                }

                if (existingImages.isNotEmpty() || newImages.isNotEmpty()) {
                    Box(modifier = Modifier.heightIn(max = 120.dp)) {
                        LazyColumn {
                            items(existingImages) { url ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(url.substringAfterLast("/").split("?").firstOrNull() ?: "Photo", style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                                    TextButton(onClick = { existingToRemove = url }) {
                                        Text("Remove", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                            }
                            items(newImages) { pair ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text("New: ${pair.second}", style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                                    TextButton(onClick = { newToRemove = pair }) {
                                        Text("Remove", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                            }
                        }
                    }
                }

                if (errorMessage != null) {
                    Text(errorMessage!!, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.error)
                }

                if (isUploading) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        CircularProgressIndicator(modifier = Modifier.size(20.dp))
                        Text("Uploading images to Firebase...", style = MaterialTheme.typography.bodySmall)
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    scope.launch {
                        isUploading = true
                        errorMessage = null
                        try {
                            for (url in removedImages) {
                                storageService.deleteOrderImage(url)
                            }

                            val uploadedUrls = mutableListOf<String>()
                            uploadedUrls.addAll(existingImages)
                            for ((bytes, _) in newImages) {
                                val url = storageService.uploadImage(bytes, StorageArea.ORDERS)
                                uploadedUrls.add(url)
                            }

                            val hasDate = dateInput.isNotBlank() && hourInput.isNotBlank() && minuteInput.isNotBlank()
                            val receivedAt = if (hasDate) "$dateInput $hourInput:$minuteInput:00" else null
                            val isActionMissing = if (hasDate) 0 else 1

                            isUploading = false
                            onConfirm(receivedAt, uploadedUrls, isActionMissing)
                        } catch (e: Exception) {
                            isUploading = false
                            errorMessage = "Upload failed: ${e.message}"
                        }
                    }
                },
                enabled = !isUploading
            ) {
                Text("Confirm Received")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismissWithoutDate) {
                Text("Dismiss / Missing Date")
            }
        },
        shape = MaterialTheme.shapes.large
    )

    existingToRemove?.let { url ->
        ConfirmDeleteDialog(
            title = "Remove photo?",
            message = "Remove this delivery photo?",
            confirmLabel = "Remove",
            onConfirm = {
                removedImages.add(url)
                existingImages.remove(url)
                existingToRemove = null
            },
            onDismiss = { existingToRemove = null }
        )
    }
    newToRemove?.let { pair ->
        ConfirmDeleteDialog(
            title = "Remove photo?",
            message = "Remove this new photo?",
            confirmLabel = "Remove",
            onConfirm = {
                newImages.remove(pair)
                newToRemove = null
            },
            onDismiss = { newToRemove = null }
        )
    }
}

@Composable
fun OrderItemRow(
    item: OrderItem,
    itemName: String,
    orderStatus: String,
    receivedQty: String,
    onReceivedQtyChange: (String) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(text = itemName, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
            val orderedDisplay = formatWhole(item.qtyOrdered)
            Text("Ordered: $orderedDisplay", style = MaterialTheme.typography.bodyMedium)
        }
        
        if (orderStatus == "Pending") {
            NumberStepper(
                value = receivedQty,
                onValueChange = onReceivedQtyChange,
                label = "Rec'd",
                step = 1.0
            )
        } else {
            val receivedDisplay = formatWhole(item.qtyReceived ?: 0.0)
            Text("Received: $receivedDisplay", style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.secondary)
        }
    }
}

@Composable
fun EditableOrderItemRow(
    itemName: String,
    supplierName: String = "",
    orderedQty: String,
    onOrderedQtyChange: (String) -> Unit,
    onRemove: () -> Unit
) {
    val currentQtyInt = (orderedQty.toDoubleOrNull() ?: 0.0).toInt()
    val itemNameLower = itemName.lowercase().trim()
    val supplierNameLower = supplierName.lowercase().trim()
    val isBlackAngus = itemNameLower.contains("black angus beef")
    val isChickenSupplier = supplierNameLower.contains("chicken")
    val stepSize = if (isBlackAngus || isChickenSupplier) 5 else 1

    Row(
        modifier = Modifier.fillMaxWidth().padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(text = itemName, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
        }
        
        QuantityStepper(
            value = currentQtyInt,
            step = stepSize,
            onValueChange = { onOrderedQtyCheck -> onOrderedQtyChange(onOrderedQtyCheck.toString()) }
        )
        
        IconButton(onClick = onRemove) {
            CloseIcon(tint = MaterialTheme.colorScheme.error)
        }
    }
}

@Composable
fun AddItemToOrderDialog(
    availableItems: List<Item>,
    onDismiss: () -> Unit,
    onItemSelect: (Item) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Add Item to Order") },
        text = {
            Box(modifier = Modifier.heightIn(max = 300.dp)) {
                if (availableItems.isEmpty()) {
                    Text("All items from this supplier are already in the order.")
                } else {
                    LazyColumn {
                        items(availableItems) { item ->
                            ListItem(
                                headlineContent = { Text(item.name, fontWeight = FontWeight.Bold) },
                                supportingContent = { Text(item.packDescription) },
                                modifier = Modifier.clickable { onItemSelect(item) }
                            )
                            HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text("CANCEL")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}

@Composable
fun NumberStepper(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    step: Double = 1.0,
    min: Double = 0.0,
    max: Double? = null,
    label: String? = null,
    stacked: Boolean = false,
) {
    val numeric = value.toDoubleOrNull() ?: 0.0

    fun applyDelta(delta: Double) {
        var next = numeric + delta
        if (next < min) next = min
        if (max != null && next > max) next = max
        onValueChange(formatDecimal(next))
    }

    val minusButton = @Composable {
        OutlinedIconButton(
            onClick = { applyDelta(-step) },
            modifier = Modifier.size(if (stacked) 44.dp else 40.dp),
            shape = MaterialTheme.shapes.small
        ) {
            Text("-", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onSurface)
        }
    }
    val plusButton = @Composable {
        OutlinedIconButton(
            onClick = { applyDelta(step) },
            modifier = Modifier.size(if (stacked) 44.dp else 40.dp),
            shape = MaterialTheme.shapes.small
        ) {
            Text(
                "+",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
        }
    }
    val valueContent = @Composable {
        BasicTextField(
            value = if (value.isBlank()) "0" else value,
            onValueChange = { raw ->
                val cleaned = raw.filter { it.isDigit() || it == '.' }
                onValueChange(cleaned)
            },
            modifier = Modifier.widthIn(min = 40.dp, max = 72.dp).padding(vertical = 4.dp),
            singleLine = true,
            textStyle = MaterialTheme.typography.titleLarge.copy(
                textAlign = TextAlign.Center,
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
                color = MaterialTheme.colorScheme.onSurface
            ),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            decorationBox = { inner ->
                Box(contentAlignment = Alignment.Center) { inner() }
            }
        )
    }

    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        if (label != null) {
            Text(label, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        if (stacked) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                plusButton()
                valueContent()
                minusButton()
            }
        } else {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                minusButton()
                valueContent()
                plusButton()
            }
        }
    }
}

@Composable
fun FreeTextAmountField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
) {
    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        if (label != null) {
            Text(label, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        OutlinedTextField(
            value = value,
            onValueChange = { raw ->
                val cleaned = raw.filter { it.isDigit() || it == '.' }
                val parts = cleaned.split('.')
                val next = if (parts.size <= 1) cleaned
                else parts[0] + "." + parts.drop(1).joinToString("").take(2)
                onValueChange(next)
            },
            modifier = Modifier.width(88.dp),
            singleLine = true,
            textStyle = MaterialTheme.typography.bodyLarge.copy(textAlign = TextAlign.Center, fontSize = 16.sp),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            shape = MaterialTheme.shapes.small
        )
    }
}

@Composable
fun QuantityStepper(
    value: Int,
    step: Int,
    onValueChange: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    NumberStepper(
        value = value.toString(),
        onValueChange = { onValueChange((it.toDoubleOrNull() ?: 0.0).toInt()) },
        modifier = modifier,
        step = step.toDouble(),
        min = 0.0
    )
}
